import { Loadable } from '@/components/loaders';
import { i18nMap } from '@/data/translationMapper';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { BaseAudience } from '@/models/audiences';
import { ConnectorFlowDTO, ConnectorInstanceSafeDTO } from '@/models/connectors';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import Form from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type StepConfig = {
  handler?: string;
  reactJsonSchemaForm?: {
    formId?: string;
    title?: string;
    description?: string;
    schema: RJSFSchema;
    uiSchema: Record<string, any>;
  };
};

type MappingsConfig = {
  schema: RJSFSchema;
  uiSchema: Record<string, any>;
} | null;

type MultiStepFlowFormProps = {
  audience: BaseAudience;
  connectorInstance: ConnectorInstanceSafeDTO;
  flow: ConnectorFlowDTO;
  paramsConfig: Record<string, StepConfig>;
  onComplete: (allParams: Record<string, any>, mappingsConfig: MappingsConfig) => void;
};

export const MultiStepFlowForm: FC<MultiStepFlowFormProps> = ({ audience, connectorInstance, flow, paramsConfig, onComplete }) => {
  const { t } = useTranslation();
  const { id: workspaceId } = useWorkspace();
  const [prepareStep, { isLoading: isLoadingStep }] = usePrepareConnectorFlowStepMutation();
  const dispatch = useAppDispatch();

  const { currentLanguage } = useCompanyValues();

  // Get sorted step keys (step1, step2, step3, ...)
  const stepKeys = useMemo(() => {
    return Object.keys(paramsConfig)
      .filter((key) => /^step\d+$/.test(key))
      .sort((a, b) => parseInt(a.replace('step', '')) - parseInt(b.replace('step', '')));
  }, [paramsConfig]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentStepSchema, setCurrentStepSchema] = useState<{
    schema: RJSFSchema;
    uiSchema: Record<string, any>;
    title?: string;
  } | null>(null);
  const [currentFormData, setCurrentFormData] = useState<Record<string, any>>({});
  const [accumulatedData, setAccumulatedData] = useState<Record<string, any>>({});
  const [isInitializing, setIsInitializing] = useState(true);
  const [lastStepMappings, setLastStepMappings] = useState<MappingsConfig>(null);

  // Use ref to track if we've initialized
  const hasInitialized = useRef(false);

  const isLastStep = currentStepIndex === stepKeys.length - 1;
  const totalSteps = stepKeys.length;

  // Load step schema from API and capture mappings if present in response
  const loadStepSchema = async (stepIndex: number, dataToSend: Record<string, any>): Promise<void> => {
    // For step N, we call POST /prepare/step{N} with data from previous steps
    // - For step1 (index 0): POST /prepare/step1 with empty data
    // - For step2 (index 1): POST /prepare/step2 with step1 data
    // - For step3 (index 2): POST /prepare/step3 with step1+step2 data
    const stepIdToCall = stepKeys[stepIndex]; // step1, step2, step3, etc.
    const bodyToSend = stepIndex === 0 ? {} : dataToSend;

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: flow.id,
        stepId: stepIdToCall,
        audienceId: audience.id,
        connectorInstanceId: connectorInstance.id,
        stepData: bodyToSend,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      // Extract schema from nested response
      const formSchema =
        translatedResponse?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.reactJsonSchemaForm ||
        translatedParamsConfig[stepKeys[stepIndex]]?.reactJsonSchemaForm;

      if (formSchema) {
        setCurrentStepSchema({
          schema: formSchema.schema,
          uiSchema: formSchema.uiSchema || {},
          title: formSchema.title,
        });
      }

      // Capture mappings if present in response (will be used when all steps are done)
      // Path: response.configuration.elements.mappings.reactJsonSchemaForm
      const mappingsSchema =
        translatedResponse?.configuration?.elements?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.mappings?.reactJsonSchemaForm;
      if (mappingsSchema?.schema) {
        setLastStepMappings({
          schema: mappingsSchema.schema,
          uiSchema: mappingsSchema.uiSchema || {},
        });
      }
    } catch (error: any) {
      // Fallback to static config
      console.error('---------Failed to load step schema, using fallback:---------', error);
      const errorMessage = error?.data?.message || 'Something went wrong, Could not Proceed further';
      dispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));

      // const fallbackSchema = paramsConfig[stepKeys[stepIndex]]?.reactJsonSchemaForm;
      // if (fallbackSchema) {
      //   setCurrentStepSchema({
      //     schema: fallbackSchema.schema,
      //     uiSchema: fallbackSchema.uiSchema || {},
      //     title: fallbackSchema.title,
      //   });
      // }
    }
  };

  // Initialize first step only once
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      setIsInitializing(true);
      await loadStepSchema(0, {});
      setIsInitializing(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextStep = async () => {
    // Merge current form data with accumulated data
    const newAccumulatedData = { ...accumulatedData, ...currentFormData };
    setAccumulatedData(newAccumulatedData);

    if (isLastStep) {
      // All steps completed - pass params and mappings (already captured from last step's load response)
      onComplete(newAccumulatedData, lastStepMappings);
    } else {
      // Move to next step and load its schema
      const nextStepIndex = currentStepIndex + 1;
      await loadStepSchema(nextStepIndex, newAccumulatedData);
      setCurrentStepIndex(nextStepIndex);
      setCurrentFormData({});
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
      // Restore previous step's data from accumulated
      // Note: This is simplified - in production you might want to store each step's data separately
      setCurrentFormData({});
    }
  };

  const isFormValid = currentFormData && Object.keys(currentFormData).length > 0;

  return (
    <div className='space-y-6'>
      {/* Step indicator */}
      <div className='flex items-center justify-between border-b border-secondary-border pb-4'>
        <div className='flex items-center gap-2'>
          {stepKeys.map((stepKey, index) => (
            <div key={stepKey} className='flex items-center'>
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  index === currentStepIndex ? 'bg-primary-600 text-white' : index < currentStepIndex ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {index < currentStepIndex ? '✓' : index + 1}
              </div>
              {index < stepKeys.length - 1 && <div className='mx-2 h-0.5 w-8 bg-gray-200' />}
            </div>
          ))}
        </div>
        <span className='text-sm text-tertiary-600'>
          {t('COMMON:step')} {currentStepIndex + 1} {t('COMMON:of')} {totalSteps}
        </span>
      </div>

      {/* Step title */}
      {currentStepSchema?.title && <h3 className='text-lg font-medium text-primary-900'>{currentStepSchema.title}</h3>}

      {/* Form */}
      <Loadable isLoading={isInitializing || isLoadingStep}>
        {currentStepSchema && (
          <Form
            schema={currentStepSchema.schema}
            uiSchema={currentStepSchema.uiSchema}
            formData={currentFormData}
            onChange={({ formData }) => setCurrentFormData(formData)}
            validator={validator}
          >
            <Fragment />
          </Form>
        )}
      </Loadable>

      {/* Navigation buttons */}
      <div className='flex justify-between pt-4'>
        <button
          type='button'
          onClick={handlePreviousStep}
          disabled={currentStepIndex === 0}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            currentStepIndex === 0 ? 'cursor-not-allowed text-gray-400' : 'text-primary-600 hover:text-primary-700'
          }`}
        >
          {t('COMMON:previous')}
        </button>
        <button
          type='button'
          onClick={handleNextStep}
          disabled={!isFormValid || isLoadingStep}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            !isFormValid || isLoadingStep ? 'cursor-not-allowed bg-gray-300 text-gray-500' : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {isLastStep ? t('COMMON:confirm') : t('COMMON:next')}
        </button>
      </div>
    </div>
  );
};
